import type { DayProgress } from '../storage/localSave';

export type InitialDayFlow =
  | {
      panel: 'none';
      mode: 'key9' | 'full';
      practice: boolean;
      statusLabel: string;
    }
  | {
      panel: 'missed';
      mode: 'key9' | 'full';
      practice: boolean;
      statusLabel: 'Key9 Missed';
    }
  | {
      panel: 'full_done';
      mode: null;
      practice: false;
      statusLabel: 'Done for today';
    };

export type MissedPanelCopy = {
  title: string;
  body: string;
  primaryButton: string;
  secondaryButton: string;
  tertiaryButton: string;
};

export type ResultActionSheetState = 'active' | 'key9_unlocked' | 'key9_missed' | 'full_done';

export type InlineKey9ActionState = ResultActionSheetState | 'key9_practice_complete';

export type ResultActionSheet = {
  visible: boolean;
  variant: 'active' | 'unlocked' | 'missed' | 'done';
  buttons: {
    practice?: string;
    continue?: string;
    share?: string;
  };
};

export type InlineKey9Actions = {
  visible: boolean;
  status: string;
  variant: 'none' | 'unlocked' | 'missed' | 'practice_complete';
  buttons: {
    practice?: string;
    continue?: string;
    share?: string;
  };
};

export function getInitialDayFlow(progress: DayProgress | null | undefined): InitialDayFlow {
  if (progress?.phase === 'full_done') {
    return {
      panel: 'full_done',
      mode: null,
      practice: false,
      statusLabel: 'Done for today'
    };
  }

  if (progress?.key9Result?.status === 'missed') {
    const hasFullProgress = progress.phase === 'full_in_progress';
    return {
      panel: 'missed',
      mode: hasFullProgress ? 'full' : 'key9',
      practice: !hasFullProgress,
      statusLabel: 'Key9 Missed'
    };
  }

  if (progress?.phase === 'key9_unlocked' || progress?.phase === 'full_in_progress') {
    return {
      panel: 'none',
      mode: 'full',
      practice: false,
      statusLabel: 'Continue today’s Sudoku'
    };
  }

  return {
    panel: 'none',
    mode: 'key9',
    practice: false,
    statusLabel: 'Today’s Key9'
  };
}

export function getMissedPanelCopy(): MissedPanelCopy {
  return {
    title: 'Key9 Missed',
    body: 'You used all 9 misses.\nYou can still keep solving as practice.',
    primaryButton: 'Try Again',
    secondaryButton: 'Continue Sudoku',
    tertiaryButton: 'Share Result'
  };
}

export function getResultActionSheet(state: ResultActionSheetState): ResultActionSheet {
  switch (state) {
    case 'key9_unlocked':
    case 'key9_missed':
      return {
        visible: false,
        variant: 'active',
        buttons: {}
      };
    case 'full_done':
      return {
        visible: true,
        variant: 'done',
        buttons: {
          share: 'Share Result'
        }
      };
    case 'active':
      return {
        visible: false,
        variant: 'active',
        buttons: {}
      };
  }
}

export function getInlineKey9Actions(state: InlineKey9ActionState): InlineKey9Actions {
  switch (state) {
    case 'key9_unlocked':
      return {
        visible: true,
        status: 'Key9 unlocked.',
        variant: 'unlocked',
        buttons: {
          continue: 'Continue Sudoku',
          share: 'Share Key9'
        }
      };
    case 'key9_missed':
      return {
        visible: true,
        status: 'Key9 missed.',
        variant: 'missed',
        buttons: {
          practice: 'Try Again',
          continue: 'Continue Sudoku',
          share: 'Share Result'
        }
      };
    case 'key9_practice_complete':
      return {
        visible: true,
        status: 'Practice complete.',
        variant: 'practice_complete',
        buttons: {
          practice: 'Try Again',
          continue: 'Continue Sudoku',
          share: 'Share Result'
        }
      };
    case 'active':
    case 'full_done':
      return {
        visible: false,
        status: '',
        variant: 'none',
        buttons: {}
      };
  }
}
